// Small building blocks shared by the Remote Punch windows, so the pin window
// and the alert window read as one design.
import React, { useState } from "react";
import { ClipboardCopy, Check } from "lucide-react";
import { accuracyQuality } from "./PunchMap";

/** A labelled value on a soft card. `wide` spans the whole grid row. */
export const InfoTile = ({ c, icon: Icon, label, children, wide = false, accent }) => (
  <div
    style={{
      gridColumn: wide ? "1 / -1" : undefined,
      minWidth: 0,
      padding: "11px 13px",
      borderRadius: 12,
      background: c.soft,
      border: `1px solid ${c.border}`,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, color: c.muted, marginBottom: 4 }}>
      {Icon && <Icon size={13} color={accent || c.muted} />}
      {label}
    </div>
    <div style={{ fontSize: 14, fontWeight: 700, color: c.text, overflowWrap: "anywhere", lineHeight: 1.45 }}>{children}</div>
  </div>
);

export const TileGrid = ({ children, min = 180 }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 10 }}>{children}</div>
);

export const SectionTitle = ({ c, icon: Icon, children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "18px 0 8px", fontSize: 12.5, fontWeight: 800, color: c.muted, letterSpacing: 0.2 }}>
    {Icon && <Icon size={15} />}
    {children}
  </div>
);

/** "± 15 m · Excellent" with the quality colour. */
export const AccuracyValue = ({ meters, t }) => {
  const q = accuracyQuality(meters);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <span><span dir="ltr">± {meters}</span> {t.meters}</span>
      <span style={{ padding: "1px 8px", borderRadius: 999, fontSize: 11.5, fontWeight: 700, color: q.color, background: `${q.color}1a` }}>
        {t.quality[q.level]}
      </span>
    </span>
  );
};

/** Coordinates with a one-click copy. */
export const Coordinates = ({ latitude, longitude, t, c }) => {
  const [copied, setCopied] = useState(false);
  const text = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — the text is still selectable */ }
  };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span dir="ltr" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 600, fontSize: 13 }}>{text}</span>
      <button
        type="button"
        onClick={copy}
        title={copied ? t.copied : t.copyCoords}
        aria-label={copied ? t.copied : t.copyCoords}
        style={{ border: "none", background: "transparent", cursor: "pointer", padding: 2, display: "flex", color: copied ? c.success : c.muted }}
      >
        {copied ? <Check size={15} /> : <ClipboardCopy size={15} />}
      </button>
    </span>
  );
};

export const primaryLinkStyle = (c) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "9px 16px",
  borderRadius: 10,
  background: c.primary,
  color: "#fff",
  fontSize: 13.5,
  fontWeight: 700,
  textDecoration: "none",
  boxShadow: "0 4px 12px rgba(37,99,235,0.28)",
});

export const formatDateTime = (at, language, withWeekday = false) => {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(language === "ar" ? "ar-EG-u-nu-latn" : "en-GB", {
    ...(withWeekday ? { weekday: "long" } : {}),
    year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit",
  });
};
